import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useConsignments } from '@/hooks/useConsignments';
import ConsignmentModal from './components/ConsignmentModal';
import ConsignmentDetailModal from './components/ConsignmentDetailModal';
import type { Consignment } from '@/services/consignments';

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: 'Activa', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  pending: { label: 'Pendiente', color: 'text-amber-700', bg: 'bg-amber-50' },
  settled: { label: 'Liquidada', color: 'text-accent-700', bg: 'bg-accent-50' },
  closed: { label: 'Cerrada', color: 'text-foreground-600', bg: 'bg-background-100' },
  returned: { label: 'Devuelta', color: 'text-red-700', bg: 'bg-red-50' },
};

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function ConsignmentsPage() {
  const { data, loading, error, refetch } = useConsignments();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailFor, setDetailFor] = useState<Consignment | null>(null);

  const statuses = ['all', ...Array.from(new Set(data.map((c) => c.status)))];

  const filtered = data.filter((c) => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.distributorName.toLowerCase().includes(q) ||
      c.notes.toLowerCase().includes(q)
    );
  });

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Mercancía en consignación</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} consignaciones registradas</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <button className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer" onClick={() => setIsModalOpen(true)}>
            <i className="ri-add-line mr-1.5"></i>Nueva Consignación
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Consignaciones totales</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{data.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Activas</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">
              {data.filter((c) => c.status === 'active').length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Pares entregados</p>
            <p className="text-xl font-bold text-accent-600 mt-1">
              {data.reduce((sum, c) => sum + c.itemCount, 0)}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex flex-wrap gap-1.5">
            {statuses.map((s) => {
              const meta = STATUS_MAP[s] || { label: s, color: 'text-foreground-600', bg: 'bg-background-100' };
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    statusFilter === s
                      ? 'bg-accent-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {s === 'all' ? 'Todas' : meta.label}
                </button>
              );
            })}
          </div>
          <div className="relative flex-1 max-w-xs ml-auto">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar por distribuidor o nota..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Distribuidor</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Estado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Pares entregados</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Fecha</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden lg:table-cell">Notas</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-14"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando consignaciones...
                    </td>
                  </tr>
                ) : error ? null : (
                  filtered.map((c) => {
                    const meta = STATUS_MAP[c.status] || {
                      label: c.status,
                      color: 'text-foreground-600',
                      bg: 'bg-background-100',
                    };
                    return (
                      <tr key={c.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                              <i className="ri-store-2-line text-accent-600"></i>
                            </div>
                            <p className="text-foreground-900 font-medium">{c.distributorName || '—'}</p>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${meta.color} ${meta.bg}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right text-foreground-600 font-medium whitespace-nowrap">
                          {c.itemCount}
                        </td>
                        <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(c.createdAt)}</td>
                        <td className="px-5 py-3 text-xs text-foreground-500 hidden lg:table-cell max-w-[240px] truncate">
                          {c.notes || '—'}
                        </td>
                        <td className="px-5 py-3">
                          <button
                            onClick={() => setDetailFor(c)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                            title="Ver factura y pagos"
                          >
                            <i className="ri-eye-line text-foreground-500"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-store-2-line text-3xl block mb-2"></i>
              <p className="text-sm">
                {data.length === 0
                  ? 'Aún no hay consignaciones registradas.'
                  : 'No se encontraron consignaciones con ese filtro.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <ConsignmentModal onClose={() => setIsModalOpen(false)} onSaved={refetch} />
      )}
      {detailFor && (
        <ConsignmentDetailModal
          consignmentId={detailFor.id}
          onClose={() => setDetailFor(null)}
          onSaved={refetch}
        />
      )}
    </AdminLayout>
  );
}